// @flow

import React, {useEffect, useMemo, useState} from "react";
import classNames from "classnames";
import type {translate} from "../../i18next";
import {
    AUTO_EXPAND_LEAF_LIMIT,
    buildFitmentTree,
    compressYears,
    countLeaves,
    fitsAllModelYears,
    type FitmentNode,
} from "../../helper/fitmentTree";
import "./FitmentTree.scss";

type FitmentTreeProps = {
    t: translate,
    brandName: string,
    fittingCarPaths?: ?(string[]),
    fitments: string[],
    // Ordered display-name segments (top-down) of the currently filtered vehicle, if any.
    filterPath?: ?(string[]),
    // Notified after expand/collapse changed the rendered height (parent keeps a fixed box height).
    onLayoutChange?: () => void,
};

const keyFor = (ancestors: string[]): string => ancestors.join(" ");

// Collects keys of every node (so collapse/expand-all can address them all).
const collectKeys = (nodes: FitmentNode[], ancestors: string[], acc: string[]): string[] => {
    nodes.forEach(node => {
        const path = [...ancestors, node.label];
        if (node.children.length > 0) acc.push(keyFor(path));
        collectKeys(node.children, path, acc);
    });
    return acc;
};

// Distinct years anywhere in a subtree (for the collapsed-header counter).
const distinctYears = (node: FitmentNode, acc: Set<number>): Set<number> => {
    node.years.forEach(y => acc.add(y));
    node.children.forEach(c => distinctYears(c, acc));
    return acc;
};

// Returns the set of node keys that lie on the active filter path, plus the key of the
// deepest matched node (to highlight). Matching is case-insensitive on the labels.
const resolveFilter = (nodes: FitmentNode[], filterPath: ?(string[])) => {
    const onPath = new Set<string>();
    let highlightKey = null;
    if (!filterPath || filterPath.length === 0) return {onPath, highlightKey};

    const norm = (s: string) => s.trim().toLowerCase();
    const walk = (siblings: FitmentNode[], ancestors: string[], depth: number) => {
        siblings.forEach(node => {
            const path = [...ancestors, node.label];
            const matchesHere = depth < filterPath.length && norm(node.label) === norm(filterPath[depth]);
            if (matchesHere) {
                const key = keyFor(path);
                onPath.add(key);
                // deepest match wins (longest prefix of the filter path)
                if (highlightKey === null || path.length > highlightKey.split(" ").length) {
                    highlightKey = key;
                }
                walk(node.children, path, depth + 1);
            }
        });
    };
    walk(nodes, [], 0);
    return {onPath, highlightKey};
};

function FitmentTree(props: FitmentTreeProps) {
    const {t, brandName, fittingCarPaths, fitments, filterPath, onLayoutChange} = props;

    const tree = useMemo(() => buildFitmentTree(fittingCarPaths, brandName), [fittingCarPaths, brandName]);
    const leafCount = useMemo(() => countLeaves(tree), [tree]);
    const allKeys = useMemo(() => collectKeys(tree, [], []), [tree]);
    const {onPath, highlightKey} = useMemo(() => resolveFilter(tree, filterPath), [tree, filterPath]);

    // Default expand state (QA feedback in #53422#note-26/27): a matched vehicle filter
    // opens ONLY the filtered path — everything else stays collapsed so the match stands
    // out. Without a filter, <= 20 leaves opens everything; above the limit all series
    // accordions start collapsed (headers with counters only) to avoid the wall of text.
    const defaultOpen = useMemo(() => {
        const filterMatched = onPath.size > 0;
        const expandAll = !filterMatched && leafCount <= AUTO_EXPAND_LEAF_LIMIT;
        const set = new Set<string>();
        const walk = (nodes: FitmentNode[], ancestors: string[]) => {
            nodes.forEach(node => {
                const path = [...ancestors, node.label];
                const key = keyFor(path);
                if (node.children.length > 0 && (expandAll || onPath.has(key))) {
                    set.add(key);
                }
                walk(node.children, path);
            });
        };
        walk(tree, []);
        return set;
    }, [tree, leafCount, onPath]);

    const [openOverrides, setOpenOverrides] = useState<{[string]: boolean}>({});

    useEffect(() => {
        if (onLayoutChange) onLayoutChange();
    }, [openOverrides, onLayoutChange]);

    const isOpen = (key: string): boolean =>
        Object.prototype.hasOwnProperty.call(openOverrides, key) ? openOverrides[key] : defaultOpen.has(key);

    const toggle = (key: string) =>
        setOpenOverrides(prev => ({...prev, [key]: !isOpen(key)}));

    const setAll = (open: boolean) =>
        setOpenOverrides(allKeys.reduce((acc, key) => ({...acc, [key]: open}), {}));

    // Fallback: no structured paths available (e.g. before reindex) -> flat list as before.
    if (!fittingCarPaths || fittingCarPaths.length === 0 || tree.length === 0) {
        return (
            <ul className="hpm-fitment-fallback">
                {fitments.map(vehicle => <li key={vehicle}>{brandName + " " + vehicle}</li>)}
            </ul>
        );
    }

    const renderYearChips = (node: FitmentNode) => {
        const chips = [...compressYears(node.years), ...node.literalYears];
        return (
            <span className="hpm-fitment-chips">
                {chips.map(chip => <span className="hpm-fitment-chip" key={chip}>{chip}</span>)}
            </span>
        );
    };

    const renderNode = (node: FitmentNode, ancestors: string[]) => {
        const path = [...ancestors, node.label];
        const key = keyFor(path);
        const hasChildren = node.children.length > 0;
        const open = isOpen(key);
        const highlighted = key === highlightKey;

        const years = [...distinctYears(node, new Set())];
        const variants = node.children.length;
        const counterParts = [];
        if (variants > 0) counterParts.push(t("productDetail.vehicleTab.variantsCount", {count: variants}));
        if (years.length > 0) counterParts.push(t("productDetail.vehicleTab.yearsCount", {count: years.length}));

        const rowClass = classNames("hpm-fitment-row", {
            "hpm-fitment-row--highlighted": highlighted,
            "hpm-fitment-row--toggle": hasChildren,
        });

        return (
            <li className="hpm-fitment-node" key={key}>
                <div
                    className={rowClass}
                    onClick={hasChildren ? () => toggle(key) : undefined}
                    onKeyDown={hasChildren ? (e => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggle(key);
                        }
                    }) : undefined}
                    role={hasChildren ? "button" : undefined}
                    tabIndex={hasChildren ? 0 : undefined}
                    aria-expanded={hasChildren ? open : undefined}
                >
                    {hasChildren &&
                        <span className={classNames("hpm-fitment-caret", {"hpm-fitment-caret--open": open})} aria-hidden="true"/>}
                    <span className="hpm-fitment-label">{node.label}</span>
                    {node.isYearLevel && renderYearChips(node)}
                    {fitsAllModelYears(node) &&
                        <span className="hpm-fitment-chip hpm-fitment-chip--all">{t("productDetail.vehicleTab.allModelYears")}</span>}
                    {hasChildren && !open && counterParts.length > 0 &&
                        <span className="hpm-fitment-counter">({counterParts.join(", ")})</span>}
                </div>
                {hasChildren && open &&
                    <ul className="hpm-fitment-children">
                        {node.children.map(child => renderNode(child, path))}
                    </ul>}
            </li>
        );
    };

    const someClosed = allKeys.some(key => !isOpen(key));

    return (
        <div className="hpm-fitment-tree">
            {allKeys.length > 0 &&
                <button
                    type="button"
                    className="hpm-fitment-expand-toggle"
                    onClick={() => setAll(someClosed)}
                >
                    {someClosed
                        ? t("productDetail.vehicleTab.expandAll")
                        : t("productDetail.vehicleTab.collapseAll")}
                </button>}
            <ul className="hpm-fitment-root">
                {tree.map(node => renderNode(node, []))}
            </ul>
        </div>
    );
}

export default FitmentTree;
